import React, { useEffect, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { useLoadScript } from "@react-google-maps/api";
import LayoutPresta from "./layoutPresta";
import { validateFrenchIBAN } from "../../utils/utils";
import { useDispatch, useSelector } from "react-redux";
import {
  FirstupdatedEtablissement,
  getMyEtablissement,
  updatedEtablissement,
} from "../../store/slices/etablissementSlice";
import { EstablishmentForm } from "../../components/etablissement/infoEtablissement";
import { toast } from "react-toastify";
import { Helmet, HelmetProvider } from "react-helmet-async";

const libraries = ["places"];

function EstablishmentInformations() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedEtablissement } = useSelector(
    (state) => state.etablissements
  );

  useEffect(() => {
    dispatch(getMyEtablissement());
  }, [dispatch]);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const validationSchema = Yup.object().shape({
    nom: Yup.string()
      .required("Le nom est requis")
      .max(50, "Le nom de l'entreprise ne doit pas dépasser 50 caractères"),
    companyAddress: Yup.string().required("L'adresse est requise"),
    description: Yup.string().max(
      1000,
      "La description ne doit pas dépasser 1000 caractères"
    ),
    // code_postal: Yup.string().required("L'adresse non valide"),
    iban: Yup.string()
      .required("L'IBAN est requis")
      .transform((value) => (value ? value.replace(/\s+/g, "") : value)),
    //.test("is-french-iban", "Veuillez entrer un IBAN français valide", validateFrenchIBAN),
  });
  const checkSiret = async (value) => {
    if (!value || value.length !== 14) return false;
    try {
      const response = await fetch(
        `https://api.insee.fr/api-sirene/3.11/siret/${value}`,
        {
          headers: {
            "X-INSEE-Api-Key-Integration": process.env.REACT_APP_CONSUMER_KEY,
          },
        }
      );
      return response.ok;
    } catch {
      return false;
    }
  };
  const handleSubmit = async (values, { setSubmitting }) => {
    const stepsStatus = JSON.parse(localStorage.getItem("stepsStatus")) || {};
    stepsStatus[1] = "completed";
    localStorage.setItem("stepsStatus", JSON.stringify(stepsStatus));

    const cleanedIban = values.iban.replace(/\s+/g, "");
    const isSiretValid = await checkSiret(values.siret);
    if (!isSiretValid) {
      toast.error("Le SIRET n'existe pas dans la base INSEE");
      return;
    }
    const formData = new FormData();

    // Append all existing fields
    formData.append("iban", cleanedIban);
    formData.append("nom", values.nom);
    formData.append("lat", values.lat?.toString());
    formData.append("lng", values.lng?.toString());
    formData.append("siret", values.siret);
    formData.append("code_postal", values.postalCode);
    formData.append("tva", values.tva);
    formData.append("adresse_postale", values.companyAddress);
    formData.append("description", values.description);
    formData.append("ville", values.city);

    // Append the CIN files
    if (values.cinRecto)
      formData.append("identity_document_front", values.cinRecto);
    if (values.cinVerso)
      formData.append("identity_document_back", values.cinVerso);

    if (selectedEtablissement) {
      try {
        await dispatch(
          FirstupdatedEtablissement({
            etablissementData: formData,
            route: `etablissements/${selectedEtablissement?.id}/first-update`,
          })
        ).unwrap();

        const stepsStatus =
          JSON.parse(localStorage.getItem("stepsStatus")) || {};
        stepsStatus[1] = "completed";
        localStorage.setItem("stepsStatus", JSON.stringify(stepsStatus));
        navigate("/type-activite");
      } catch (err) {
        // console.error("Update failed:", err);
        toast.error("Échec de la mise à jour: " + (err || "Erreur inconnue"));
      } finally {
        setSubmitting(false); // This ensures isSubmitting is reset in all cases
      }
    } else {
      setSubmitting(false);
    }
  };

  const getInitialValues = () => ({
    nom: selectedEtablissement?.nom || "",
    description: selectedEtablissement?.description || "",
    iban: selectedEtablissement?.iban || "",
    companyAddress: selectedEtablissement?.adresse_postale || "",
    lat: selectedEtablissement?.lat || null,
    lng: selectedEtablissement?.lng || null,
    postalCode: selectedEtablissement?.code_postal || null,
    city: selectedEtablissement?.ville || null,
  });

  return (
    <Formik
      initialValues={getInitialValues()}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true} // This allows Formik to update when initialValues change
    >
      {({
        submitForm,
        values,
        errors,
        touched,
        isSubmitting,
        setFieldTouched,
        setFieldValue,
      }) => {
        const handleFooterSubmit = () => {
          // console.log("Clicked Save",values);
          // Marquer les champs comme touchés pour déclencher les erreurs
          setFieldTouched("nom", true);
          setFieldTouched("companyAddress", true);
          setFieldTouched("iban", true);
          submitForm();
        };

        return (
          <LayoutPresta
            currentStep={1}
            nextStep={"/type-activite"}
            handleSubmit={handleFooterSubmit}
            disabled={isSubmitting}
            btn={false}
          >
            <HelmetProvider>
              <Helmet>
                <title>Inscription Prestataire </title>
                <meta
                  name="description"
                  content="Inscription Prestataire étape informations entreprise"
                />
              </Helmet>
            </HelmetProvider>
            <div className="col-md-6 mx-auto mt-48">
              <EstablishmentForm
                from="afterRegister"
                isSubmitting={isSubmitting}
              />
            </div>
            <div className="ht-100"></div>
          </LayoutPresta>
        );
      }}
    </Formik>
  );
}

export default EstablishmentInformations;
